"use client";

import * as chrono from "chrono-node";
import * as React from "react";
import { Button } from "../button";
import {
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "../command";
import { KeyboardShortcuts } from "../keyboard-shortcuts/keyboard-shortcuts";
import { cn } from "../lib/utils";
import { InlineDateTimePicker } from "./datetime-picker";

interface Option {
	label: string;
	value: string;
	date: Date;
}

export interface SmartDateTimePickerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSelect: (date: Date) => void;
	className?: string;
}

const defaultSuggestions = [
	"In 2 hours",
	"Tomorrow morning",
	"Tomorrow evening",
	"In 2 days",
	"This Saturday",
	"Next Monday",
];

function generateDate(suggestion: string): Date {
	const result = chrono.parseDate(suggestion);
	if (!result) {
		throw new Error(`Invalid date suggestion: ${suggestion}`);
	}
	return result;
}

function getDefaultOptions(): Option[] {
	return defaultSuggestions.map((suggestion) => ({
		label: suggestion,
		value: suggestion.toLowerCase().replace(/\s+/g, "-"),
		date: generateDate(suggestion),
	}));
}

export function SmartDateTimePicker({
	open,
	onOpenChange,
	onSelect,
	className,
}: SmartDateTimePickerProps) {
	const [inputValue, setInputValue] = React.useState("");
	const [suggestions, setSuggestions] = React.useState<Option[]>([]);
	const defaultOptions = getDefaultOptions();
	const [customDate, setCustomDate] = React.useState<Date>(new Date());
	const [showDatePicker, setShowDatePicker] = React.useState(false);

	const handleInputChange = React.useCallback(
		(value: string) => {
			setInputValue(value);

			if (!value.length) {
				setSuggestions(defaultOptions);
				return;
			}

			// Try to parse the input as a date first
			const parsedDate = chrono.parseDate(value);
			if (parsedDate) {
				setSuggestions([
					{
						label: value,
						value: "custom",
						date: parsedDate,
					},
				]);
				return;
			}

			// If no date could be parsed, check for matching default suggestions
			const matchingDefaults = defaultOptions.filter((option) =>
				option.label.toLowerCase().includes(value.toLowerCase()),
			);

			if (matchingDefaults.length > 0) {
				setSuggestions(matchingDefaults);
			} else {
				setSuggestions([]);
			}
		},
		[defaultOptions],
	);

	const handleSelect = React.useCallback(
		(option: Option) => {
			onSelect(option.date);
			onOpenChange(false);
		},
		[onSelect, onOpenChange],
	);

	const handleCustomDateSelect = React.useCallback(() => {
		setShowDatePicker(true);
	}, []);

	const handleDateChange = React.useCallback((date: Date | undefined) => {
		if (date) {
			setCustomDate(date);
		}
	}, []);

	const handleDateConfirm = React.useCallback(() => {
		onSelect(customDate);
		onOpenChange(false);
	}, [customDate, onSelect, onOpenChange]);

	React.useEffect(() => {
		if (open) {
			setInputValue("");
			setSuggestions(defaultOptions);
			setShowDatePicker(false);
		}
	}, [open, defaultOptions]);

	return (
		<CommandDialog
			open={open}
			onOpenChange={onOpenChange}
			className={cn("max-w-[350px]", className)}
			commandProps={{
				shouldFilter: false,
				loop: true,
				defaultValue:
					defaultOptions.length > 0 ? defaultOptions[0].value : undefined,
			}}
		>
			{!showDatePicker ? (
				<>
					<CommandInput
						placeholder="Try typing 'in 3h' or 'fri at 4pm'"
						value={inputValue}
						onValueChange={handleInputChange}
						iconKind="time"
					/>
					<CommandList>
						<CommandEmpty>
							<CommandItem
								onSelect={handleCustomDateSelect}
								className="flex items-center gap-2"
							>
								<span>Pick a time</span>
							</CommandItem>
						</CommandEmpty>
						<CommandGroup>
							{suggestions.map((option) => (
								<CommandItem
									key={option.value}
									onSelect={() => handleSelect(option)}
									className="flex items-center gap-2"
									value={option.value}
								>
									<span>{option.label}</span>
									<span className="ml-auto text-sm text-surface-icon">
										{option.date.toLocaleDateString(undefined, {
											weekday: "short",
											month: "short",
											day: "numeric",
											hour: "numeric",
											minute: "numeric",
										})}
									</span>
								</CommandItem>
							))}
							<CommandItem
								onSelect={handleCustomDateSelect}
								className="flex items-center gap-2"
							>
								<span>Pick a time</span>
							</CommandItem>
						</CommandGroup>
					</CommandList>
				</>
			) : (
				<div>
					<div className="mt-8">
						<InlineDateTimePicker
							granularity="minute"
							value={customDate}
							onChange={handleDateChange}
						/>
					</div>
					<div className="p-4 flex justify-end">
						<Button onClick={handleDateConfirm}>Select</Button>
					</div>
				</div>
			)}
			<KeyboardShortcuts
				layerName="date_picker"
				shortcuts={[
					{
						key: {
							value: ["Escape"],
							modifiers: [],
						},
						handler: () => {
							onOpenChange(false);
						},
						pop: true,
					},
				]}
			/>
		</CommandDialog>
	);
}
